import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { LuKeyRound } from 'react-icons/lu'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import DialogActions from '@mui/material/DialogActions'
import Stack from '@mui/material/Stack'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Alert from '@mui/material/Alert'
import Divider from '@mui/material/Divider'
import Switch from '@mui/material/Switch'
import Tooltip from '@mui/material/Tooltip'
import FormControlLabel from '@mui/material/FormControlLabel'
import CircularProgress from '@mui/material/CircularProgress'

import { supabase } from '../supabaseClient'
import { callTool } from '../lib/tools'
import useOrganizations, { orgLabel } from '../models/useOrganizations'
import useBilling, { isFreePlan } from '../models/useBilling'
import { useOrgRole } from '../hooks/useOrgRole'
import { useModalClose } from '../hooks/useModalClose'
import Modal from './Modal'

const CONTACT_URL = 'mailto:sales@nobgp.com?subject=noBGP%20SSO'

type Props = { inline?: boolean }

export default function OrgSSO({ inline }: Readonly<Props>) {
  const { open, handleClose } = useModalClose('/', inline)
  const { activeOrgId, organizations } = useOrganizations()
  const { can } = useOrgRole()
  const canManage = can('sso.manage') // Owner only — the router re-checks (ActionManageSSO)
  const plan = useBilling(s => s.plan)
  const isFree = isFreePlan(plan) // free tier gets an upsell, not the controls

  const activeOrg = organizations.find(o => o.id === activeOrgId)

  const [connected, setConnected] = useState(false) // organizations.workos_org_id is mapped
  const [enforced, setEnforced] = useState(false) // organizations.sso_enforced
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState<string>() // identifier of the in-flight action
  const [error, setError] = useState<string>()
  const [portalUrl, setPortalUrl] = useState<string>() // last-minted Admin Portal link, for the open-it fallback
  const [confirmEnforce, setConfirmEnforce] = useState(false)

  // Page-scoped read of the active org's SSO state. RLS scopes `organizations`
  // to the caller's orgs, so we only ever see our own row. No realtime — SSO
  // state only ever changes from this screen, so we just refetch after a write.
  const load = useCallback(async () => {
    if (!activeOrgId) return
    setLoading(true)
    setPortalUrl(undefined) // a portal link is org- and session-scoped; drop it when the org changes
    const { data, error: err } = await supabase
      .from('organizations')
      .select('workos_org_id, sso_enforced')
      .eq('id', activeOrgId)
      .single()
    if (err) setError(err.message)
    else if (data) {
      setConnected(!!data.workos_org_id)
      setEnforced(!!data.sso_enforced)
    }
    setLoading(false)
  }, [activeOrgId])

  useEffect(() => {
    void load()
  }, [load])

  const run = async (id: string, fn: () => Promise<void>) => {
    setError(undefined)
    setBusy(id)
    try {
      await fn()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Something went wrong')
    } finally {
      setBusy(undefined)
    }
  }

  // Mint a WorkOS Admin Portal link and open it in a new tab. The IdP itself
  // (OIDC or SAML) is connected inside the portal, out of band; on return the
  // org is mapped (workos_org_id set), which is all the app can observe.
  const onConfigure = () =>
    run('configure', async () => {
      if (!activeOrgId) return
      const res = await callTool<{ portal_url: string; workos_org_id: string }>('org_sso_setup', {
        org_id: activeOrgId,
      })
      setConnected(true)
      // Best-effort auto-open; the visible link below is the reliable path since a
      // window.open() after an await is often swallowed by the popup blocker.
      window.open(res.portal_url, '_blank', 'noreferrer')
      setPortalUrl(res.portal_url)
    })

  // Toggle the per-org login policy. The router refuses enforce=true without a
  // mapped provider (failed_precondition); the Switch is disabled until then to
  // mirror that, so the only path here is a valid one.
  const applyEnforced = (next: boolean) =>
    run('enforce', async () => {
      if (!activeOrgId) return
      await callTool('org_sso_set_enforced', { org_id: activeOrgId, enforced: next })
      setEnforced(next)
    })

  // `connected` only means the org is mapped to a WorkOS org — the app can't see
  // whether a real IdP connection exists behind it. Enabling enforcement with no
  // working connection locks every member out of login, so confirm first.
  // Disabling is always safe (it restores password/social login), so it goes
  // straight through.
  const onToggleEnforced = (next: boolean) => {
    if (next) setConfirmEnforce(true)
    else void applyEnforced(false)
  }

  return (
    <Modal
      open={open}
      inline={inline}
      maxWidth="sm"
      onClose={inline ? undefined : handleClose}
      color="secondary.main"
      icon={
        <Box
          sx={{
            width: 54,
            height: 54,
            borderRadius: '50%',
            bgcolor: 'secondary.main',
            color: 'background.default',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: 2,
            borderColor: 'background.default',
          }}
        >
          <LuKeyRound size={28} />
        </Box>
      }
    >
      <DialogContent>
        <Stack spacing={3}>
          <Stack
            direction="row"
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
            <Typography variant="subtitle1" color="secondary" sx={{
              flexGrow: 1
            }}>
              Single sign-on
            </Typography>
            {activeOrg && <Typography variant="code">{orgLabel(activeOrg)}</Typography>}
          </Stack>

          {!canManage && <Alert severity="info">Only the organization Owner can configure SSO.</Alert>}
          {error && (
            <Alert severity="error" onClose={() => setError(undefined)}>
              {error}
            </Alert>
          )}

          {activeOrg?.personal ? (
            <Alert severity="info">
              Single sign-on applies to organizations with more than one member. Your personal
              organization signs in with your own account.
            </Alert>
          ) : isFree ? (
            <Upsell />
          ) : loading ? (
            <Stack
              sx={{
                alignItems: 'center',
                py: 3
              }}>
              <CircularProgress size="1.5rem" />
            </Stack>
          ) : (
            <>
              {/* Status */}
              <Stack direction="row" spacing={1} sx={{
                alignItems: 'center'
              }}>
                <Chip
                  size="small"
                  color={connected ? 'success' : 'default'}
                  variant={connected ? 'filled' : 'outlined'}
                  label={connected ? 'SSO set up' : 'Not set up'}
                />
                {enforced && <Chip size="small" color="secondary" label="Enforced" />}
              </Stack>

              {/* Connect / manage the IdP via the Admin Portal */}
              <Box>
                <Typography variant="subtitle2" sx={{
                  color: 'text.secondary'
                }}>
                  Identity provider
                </Typography>
                <Typography variant="caption" sx={{
                  color: 'text.secondary'
                }}>
                  Connect an OIDC or SAML provider (Google, Okta, Entra ID, …) in the WorkOS Admin Portal. The
                  connection is managed there; this page shows your setup status and lets you enforce it.
                </Typography>
                <Box sx={{
                  mt: 1
                }}>
                  <Button
                    variant={connected ? 'outlined' : 'contained'}
                    color="secondary"
                    disabled={!canManage || !!busy}
                    onClick={onConfigure}
                  >
                    {busy === 'configure' ? (
                      <CircularProgress size="1rem" />
                    ) : connected ? (
                      'Manage identity provider'
                    ) : (
                      'Connect identity provider'
                    )}
                  </Button>
                </Box>
                {portalUrl && (
                  <Alert
                    severity="info"
                    sx={{ mt: 1.5 }}
                    action={
                      <Button color="inherit" size="small" href={portalUrl} target="_blank" rel="noreferrer">
                        Open portal
                      </Button>
                    }
                  >
                    Opening the WorkOS Admin Portal in a new tab. If it didn’t open, use the button. Connect your
                    identity provider there, then return.
                  </Alert>
                )}
              </Box>

              <Divider />

              {/* Enforcement */}
              <Box>
                <Typography variant="subtitle2" sx={{
                  color: 'text.secondary'
                }}>
                  Enforce SSO
                </Typography>
                <Typography variant="caption" sx={{
                  color: 'text.secondary'
                }}>
                  When on, members must sign in through your identity provider; password and social logins are
                  rejected for this organization. Connect a provider first.
                </Typography>
                <Box sx={{
                  mt: 1
                }}>
                  <Tooltip
                    arrow
                    placement="top"
                    title="Connect an identity provider before enforcing SSO"
                    disableHoverListener={connected}
                  >
                    <span>
                      <FormControlLabel
                        control={
                          <Switch
                            color="secondary"
                            checked={enforced}
                            disabled={!canManage || !connected || !!busy}
                            onChange={(_, v) => onToggleEnforced(v)}
                          />
                        }
                        label={busy === 'enforce' ? <CircularProgress size="1rem" /> : enforced ? 'SSO required' : 'SSO optional'}
                      />
                    </span>
                  </Tooltip>
                </Box>
              </Box>

              {/* Lock-out guard on enabling enforcement (see onToggleEnforced). */}
              <Modal open={confirmEnforce} onClose={() => setConfirmEnforce(false)} color="secondary.main">
                <DialogTitle variant="subtitle1" color="secondary">
                  Enforce SSO?
                </DialogTitle>
                <DialogContent>
                  <Typography variant="body2">
                    Members will only be able to sign in through your identity provider. If the provider isn’t fully
                    connected in the WorkOS portal yet, members — and you, on your next sign-in — can be locked out.
                    You can turn enforcement off again at any time.
                  </Typography>
                </DialogContent>
                <DialogActions>
                  <Button variant="text" sx={{ color: 'GrayText' }} onClick={() => setConfirmEnforce(false)}>
                    Cancel
                  </Button>
                  <Button
                    autoFocus
                    variant="contained"
                    color="secondary"
                    onClick={() => {
                      setConfirmEnforce(false)
                      void applyEnforced(true)
                    }}
                  >
                    Enforce SSO
                  </Button>
                </DialogActions>
              </Modal>
            </>
          )}
        </Stack>
      </DialogContent>
    </Modal>
  )
}

// Free-tier orgs see this in place of the controls — SSO is a paid feature.
function Upsell() {
  return (
    <Box
      sx={({ palette, shape }) => ({
        p: 2,
        borderRadius: `${shape.borderRadius}px`,
        border: '1px solid',
        borderColor: palette.divider,
      })}
    >
      <Stack spacing={1.5}>
        <Typography variant="subtitle2">SSO is a paid feature</Typography>
        <Typography variant="body2" sx={{
          color: 'text.secondary'
        }}>
          Single sign-on with your own identity provider (OIDC or SAML) and login enforcement are available on
          paid plans. Upgrade your organization to connect an IdP.
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button variant="contained" color="secondary" component={Link} to="/billing">
            View plans
          </Button>
          <Button variant="text" onClick={() => window.open(CONTACT_URL, '_blank', 'noreferrer')}>
            Contact sales
          </Button>
        </Stack>
      </Stack>
    </Box>
  )
}
